import {Component, input, OnInit} from '@angular/core';
import {Button} from 'primeng/button';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Select} from 'primeng/select';
import {Tag} from 'primeng/tag';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {AdminService, AtoTier, FullUserDetails, PermissionScope, UserGroupDetails, UserType} from '../../../../api';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {TaAppStore} from '../../../../state/clcta-store';
import {Tooltip} from 'primeng/tooltip';
import {PermissionService} from '../../../../services/permission.service';
import {getUserTypeSuffix} from '../../../../util/general.utils';

@Component({
  selector: 'manage-users-modal',
  imports: [
    Button,
    FormsModule,
    ReactiveFormsModule,
    Select,
    Tag,
    ToggleSwitch,
    Tooltip,
  ],
  templateUrl: './manage-users-modal.component.html',
  styleUrl: './manage-users-modal.component.scss',
})
export class ManageUsersModalComponent implements OnInit {
  modalData = input<FullUserDetails>();
  tierList = input<AtoTier[]>([]);

  localData: FullUserDetails = {
    name: '',
    email: '',
    manager: undefined,
    tierId: undefined,
    isActive: undefined,
    isEnabled: undefined,
    permissionSummary: {
      groups: []
    }
  };

  groupControl = new FormControl<UserGroupDetails | null>(null);
  allGroups: UserGroupDetails[] = [];
  selectedTier: AtoTier | undefined = undefined;

  isSystemAdmin: boolean = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public store: TaAppStore,
    private adminService: AdminService,
    private permissionsService: PermissionService,
  ) {}

  ngOnInit() {
    this.isSystemAdmin = this.permissionsService.isSystemAdmin();
    this.adminService.getAllUserGroups().subscribe((response) => {
      if (response.success && response.data) {
        const selectedIds = new Set((this.localData.permissionSummary?.groups ?? []).map((g) => g.id));
        this.allGroups = response.data.filter((g) => !selectedIds.has(g.id) && this.allowedCode(g.code ?? ''));
      }
    });

    if (!!this.modalData()) {
      this.localData = {
        ...this.modalData()!,
        permissionSummary: this.modalData()!.permissionSummary
          ? {
              ...this.modalData()!.permissionSummary,
              groups: this.modalData()!.permissionSummary?.groups?.map((g) => ({...g})) ?? [],
            }
          : undefined,
      };
      this.selectedTier = this.tierList().find((t) => t.id === this.localData.tierId);
    }
  }

  get availableGroups(): UserGroupDetails[] {
    const selectedIds = new Set((this.localData.permissionSummary?.groups ?? []).map((g) => g.id));
    return this.allGroups.filter((g) => !selectedIds.has(g.id) && this.allowedCode(g.code ?? ''));
  }

  allowedCode(code?: string): boolean {
    if (code === 'allUsers') {
      return false;
    }
    if (!this.isSystemAdmin && code === 'systemAdmins' ) {
      return false;
    }
    return true;
  }

  canManageUsers(): boolean {
    return this.permissionsService.canManageUsers()
  }

  addSelectedGroup(event: any): void {
    let group = event.value as UserGroupDetails;
    if (this.localData.permissionSummary?.groups) {
      this.localData.permissionSummary.groups = [...this.localData.permissionSummary.groups, group];
      this.groupControl.reset();
    }
  }

  removeGroup(group: UserGroupDetails): void {
    if (this.localData.permissionSummary?.groups) {
      this.localData.permissionSummary.groups = this.localData.permissionSummary.groups.filter((g) => g !== group);
    }
  }

  get activePermissions(): { key: string; scopes: PermissionScope[] }[] {
    const permissions = this.localData.permissionSummary?.permissions;
    if (!permissions) return [];

    return Object.entries(permissions)
      .filter(([_, scopes]) => scopes && scopes.length > 0)
      .map(([key, scopes]) => ({ key, scopes: scopes as PermissionScope[] }))
      .sort((a, b) => {
        if (a.key === 'All') return -1;
        if (b.key === 'All') return 1;
        return a.key.localeCompare(b.key);
      });
  }

  get canSubmit(): boolean {
    return !!this.localData.isEnabled;
  }

  set canSubmit(value: boolean) {
    this.localData.isEnabled = value;
  }

  closeDialog(data?: FullUserDetails): void {
    if (data) {
      data.tierId = this.selectedTier?.id ?? data.tierId;
      if (!data.permissionSummary) {
        data.permissionSummary = {};
      }
    }
    this.ref.close(data);
  }

  protected readonly getUserTypeSuffix = getUserTypeSuffix;
  protected readonly UserType = UserType;
}
